import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getSubscriptionTiers, getSubscriptionTiersList, createCheckout, getCheckoutStatus, getSubscriptionHistory, cancelSubscription } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { format, parseISO } from 'date-fns';
import { 
  Crown,
  Check,
  ArrowRight,
  CreditCard,
  Loader2,
  History,
  XCircle,
  CheckCircle,
  Clock,
  Dumbbell,
  Swords,
  Star,
  Trophy,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

// Base tier configurations (prices updated dynamically from API)
const BASE_TIER_CONFIG = {
  bjj: {
    id: 'bjj',
    name: 'BJJ',
    price: 129.99, // Default founders price
    icon: Dumbbell,
    color: 'blue',
    description: 'For Brazilian Jiu-Jitsu academies',
    features: [
      'Fighter Profiles',
      'Training Camps',
      'BJJ Assessments (8 Categories)',
      'Belt Tracking',
      'Competition Records',
      'Fighter Portal',
      'Video Uploads'
    ]
  },
  mma: {
    id: 'mma',
    name: 'MMA',
    price: 159.99, // Default founders price
    icon: Swords,
    color: 'red',
    popular: true,
    description: 'For Mixed Martial Arts gyms',
    features: [
      'Fighter Profiles',
      'Training Camps',
      'MMA Assessments (12 Categories)',
      'Fight Schedule & Results',
      'Weight Cut Tracking',
      'Fighter Portal',
      'Video Uploads',
      'Fight Week Mode'
    ]
  },
  hybrid: {
    id: 'hybrid',
    name: 'Hybrid',
    price: 199.99, // Default founders price
    icon: Crown,
    color: 'yellow',
    description: 'Complete BJJ + MMA solution',
    features: [
      'All BJJ Features',
      'All MMA Features',
      'Multi-Sport Athletes',
      'Toggle Assessment Types',
      'Priority Support',
      'Advanced Analytics',
      'Custom Branding',
      'API Access'
    ]
  }
};

export const SubscriptionPage = () => {
  const { gym, refreshGym, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Founders pricing state
  const [isFoundersPricing, setIsFoundersPricing] = useState(true);
  const [foundersSpotsRemaining, setFoundersSpotsRemaining] = useState(25);
  const [foundersSpotsTotal, setFoundersSpotsTotal] = useState(25);
  const [pricingTiers, setPricingTiers] = useState(BASE_TIER_CONFIG);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // Fetch current pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await getSubscriptionTiersList();
        setIsFoundersPricing(response.data.is_founders_pricing || false);
        setFoundersSpotsRemaining(response.data.founders_spots_remaining || 0);
        setFoundersSpotsTotal(response.data.founders_spots_total || 25);
        
        // Build pricing tiers from API response
        const apiTiers = response.data.tiers || {};
        const mergedTiers = {};
        Object.keys(BASE_TIER_CONFIG).forEach(key => {
          mergedTiers[key] = {
            ...BASE_TIER_CONFIG[key],
            price: apiTiers[key]?.amount || BASE_TIER_CONFIG[key].price,
            apiFeatures: apiTiers[key]?.features || []
          };
        });
        setPricingTiers(mergedTiers);
        setPricingLoaded(true);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Fall back to base config (already initialized)
        setPricingLoaded(true);
      }
    };
    fetchPricing();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadHistory();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const loadHistory = async () => {
    try {
      const response = await getSubscriptionHistory();
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    setProcessingPayment(true);
    
    try {
      const response = await getCheckoutStatus(sid);
      
      if (response.data.status === 'paid') {
        toast.success('Subscription activated!');
        await refreshGym();
        setProcessingPayment(false);
        navigate('/subscription', { replace: true });
      } else if (response.data.status === 'unpaid' && attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setProcessingPayment(false);
        navigate('/subscription', { replace: true });
      }
    } catch (error) {
      setProcessingPayment(false);
      navigate('/subscription', { replace: true });
    }
  };

  const handleSubscribe = async (tierId) => {
    if (!isAdmin) {
      toast.error('Only admins can manage subscriptions');
      return;
    }

    setLoading(true);
    try {
      const response = await createCheckout({
        tier: tierId,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start checkout'));
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await cancelSubscription();
      toast.success('Subscription will be cancelled at the end of billing period');
      await refreshGym();
      setCancelDialogOpen(false);
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  // Determine current tier based on sport_type or subscription_tier
  const getCurrentTier = () => {
    const sportType = gym?.sport_type?.toLowerCase();
    const subTier = gym?.subscription_tier?.toLowerCase();
    
    // Check sport_type first (new system)
    if (sportType && ['bjj', 'mma', 'hybrid'].includes(sportType)) {
      return sportType;
    }
    // Fall back to subscription_tier mapping (old system)
    if (subTier === 'gold') return 'mma';
    if (subTier === 'silver') return 'bjj';
    return null;
  };

  const currentTier = getCurrentTier();
  const isCurrentTier = (tierId) => currentTier === tierId && gym?.subscription_status === 'active';
  const hasActiveSubscription = gym?.subscription_status === 'active' && currentTier;

  const getColorClasses = (color, isActive) => {
    if (isActive) {
      return 'bg-zinc-900 border-green-600';
    }
    switch (color) {
      case 'blue':
        return 'bg-zinc-900/50 border-blue-600/50 hover:border-blue-600';
      case 'red':
        return 'bg-zinc-900 border-red-600 shadow-lg shadow-red-600/10';
      case 'yellow':
        return 'bg-zinc-900/50 border-yellow-600/50 hover:border-yellow-600';
      default:
        return 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700';
    }
  };

  const getCheckColor = (color) => {
    switch (color) {
      case 'blue': return 'bg-blue-600/20 text-blue-500';
      case 'red': return 'bg-red-600/20 text-red-500';
      case 'yellow': return 'bg-yellow-600/20 text-yellow-500';
      default: return 'bg-zinc-700 text-zinc-400';
    }
  };

  const getButtonClasses = (color, isActive) => {
    if (isActive) {
      return 'bg-zinc-800 text-zinc-500 cursor-not-allowed';
    }
    switch (color) {
      case 'blue': return 'bg-blue-600 hover:bg-blue-700 text-white';
      case 'red': return 'bg-red-600 hover:bg-red-700 text-white';
      case 'yellow': return 'bg-yellow-600 hover:bg-yellow-700 text-black';
      default: return 'bg-zinc-700 hover:bg-zinc-600 text-white';
    }
  };

  if (processingPayment) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20" data-testid="processing-payment">
          <Loader2 className="w-12 h-12 text-red-600 animate-spin mb-4" />
          <h2 className="font-heading text-2xl font-bold text-white uppercase tracking-tight mb-2">
            Processing Payment
          </h2>
          <p className="text-zinc-400">Please wait while we confirm your subscription...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Use pricing tiers from state (initialized with base config, updated from API)
  const SPORT_TIERS = pricingTiers;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="subscription-page">
        {/* Founders Banner */}
        {isFoundersPricing && foundersSpotsRemaining > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-lg bg-gradient-to-r from-yellow-600/20 via-yellow-500/10 to-yellow-600/20 border border-yellow-600/50"
            data-testid="founders-banner"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-600/20 rounded-full">
                  <Trophy className="w-8 h-8 text-yellow-500" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-yellow-500 uppercase tracking-tight flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Founding Member Pricing
                  </h3>
                  <p className="text-zinc-300">
                    Lock in exclusive lifetime rates - limited to first 25 gyms
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-yellow-500">
                  {foundersSpotsRemaining}
                </div>
                <div className="text-sm text-zinc-400">spots remaining</div>
                <div className="mt-1 w-full bg-zinc-700 rounded-full h-2">
                  <div 
                    className="bg-yellow-500 h-2 rounded-full transition-all"
                    style={{ width: `${((foundersSpotsTotal - foundersSpotsRemaining) / foundersSpotsTotal) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="text-center flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">
              {isFoundersPricing ? '🏆 Founders Pricing Active' : 'Choose Your Plan'}
            </p>
            <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight mb-4">
              Subscription Plans
            </h1>
            <p className="text-zinc-400 text-lg">
              {isFoundersPricing 
                ? 'Lock in exclusive founding member rates - prices increase after 25 gyms!'
                : 'Select the plan that matches your gym\'s training focus'
              }
            </p>
          </div>
          {isAdmin && history.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowHistory(!showHistory)}
              className="border-zinc-700 text-zinc-300"
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              {showHistory ? 'Hide History' : 'Payment History'}
            </Button>
          )}
        </div>

        {/* Current Subscription Banner */}
        {hasActiveSubscription && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-green-600/20 border border-green-600/50 rounded-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-400">
                  You're currently on the <strong className="uppercase">{SPORT_TIERS[currentTier]?.name || currentTier}</strong> plan
                </span>
              </div>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCancelDialogOpen(true)}
                  className="text-zinc-400 hover:text-red-500"
                  data-testid="cancel-subscription-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Payment History */}
        {showHistory && history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 bg-zinc-900/50 border border-zinc-800 rounded-sm p-6"
          >
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-zinc-500" />
              Payment History
            </h3>
            <div className="space-y-3">
              {history.map((transaction, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                  <div className="flex items-center gap-3">
                    {transaction.status === 'paid' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    )}
                    <div>
                      <p className="text-white font-medium capitalize">{transaction.tier} Plan</p>
                      <p className="text-zinc-500 text-sm">
                        {format(parseISO(transaction.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">${(transaction.amount / 100).toFixed(2)}</p>
                    <p className={cn(
                      "text-xs uppercase",
                      transaction.status === 'paid' ? 'text-green-500' : 'text-yellow-500'
                    )}>
                      {transaction.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.values(SPORT_TIERS).map((tier, index) => {
            const Icon = tier.icon;
            const isActive = isCurrentTier(tier.id);
            
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={cn(
                  "relative p-6 rounded-sm border transition-all",
                  getColorClasses(tier.color, isActive)
                )}
                data-testid={`tier-card-${tier.id}`}
              >
                {/* Badge */}
                {isActive ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-green-600 text-white text-xs uppercase font-bold px-3 py-1 rounded-sm">
                      Current Plan
                    </span>
                  </div>
                ) : tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-red-600 text-white text-xs uppercase font-bold px-3 py-1 rounded-sm flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={cn(
                      "w-6 h-6",
                      tier.color === 'blue' ? 'text-blue-500' :
                      tier.color === 'red' ? 'text-red-500' :
                      'text-yellow-500'
                    )} />
                    <h3 className="font-heading text-2xl font-bold text-white uppercase tracking-tight">
                      {tier.name}
                    </h3>
                  </div>
                  <p className="text-zinc-500 text-sm mb-4">{tier.description}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold text-white">${tier.price?.toFixed(2) || '0.00'}</span>
                    <span className="text-zinc-400">/month</span>
                  </div>
                  {isFoundersPricing && (
                    <div className="mt-2 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      <span className="text-xs text-yellow-500 font-medium">Founding Member Rate</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-zinc-300 text-sm">
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0",
                        getCheckColor(tier.color)
                      )}>
                        <Check className="w-3 h-3" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={loading || isActive}
                  className={cn(
                    "w-full h-12 uppercase font-heading font-bold tracking-wide",
                    getButtonClasses(tier.color, isActive)
                  )}
                  data-testid={`subscribe-${tier.id}-btn`}
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {isActive ? 'Current Plan' : `Subscribe to ${tier.name}`}
                  {!isActive && !loading && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Payment Info */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <CreditCard className="w-4 h-4" />
            <span>Secure payment powered by Stripe</span>
          </div>
        </div>

        {/* Cancel Subscription Dialog */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Cancel Subscription</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to cancel your {SPORT_TIERS[currentTier]?.name || currentTier} subscription? 
                You'll lose access to premium features at the end of your billing period.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Keep Subscription
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      navigate(`/subscription?session_id=${sessionId}`, { replace: true });
    } else {
      navigate('/subscription', { replace: true });
    }
  }, [sessionId, navigate]);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;
